import { useEffect, useState } from "react";
import { useSearchParams, useLocation, useNavigate, matchPath } from "react-router-dom";
import { isAdmin } from "@prism/shared/constants";
import { apiFetch } from "../lib/api.js";
import { isDashboardPath } from "../lib/pages.js";
import { useAuth } from "../context/AuthContext.jsx";

/**
 * Sidebar client switcher — admin-only. Lets BMG staff view any client's
 * dashboard without logging out. Selection is persisted in the URL
 * (?clientId=<id>), which the backend's auth middleware honors.
 *
 * Rendered as a compact select above the BusinessCard. Hidden for OWNER
 * and SALES users; even with the switcher visible, non-admin selection
 * would be ignored by the backend (see middleware/auth.js).
 */
export function ClientSwitcher() {
  const { user, client: homeClient } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  // Admin client-detail pages (/admin/clients/:id) read their client from the
  // path, not from ?clientId=. Detect that route so the switcher drives the
  // path segment instead. "/admin/clients/new" is the create form, not a detail.
  const detailMatch = matchPath("/admin/clients/:id", location.pathname);
  const detailId =
    detailMatch && detailMatch.params.id !== "new" ? detailMatch.params.id : null;

  useEffect(() => {
    if (!isAdmin(user?.role)) return;
    let cancelled = false;
    setLoading(true);
    apiFetch("/api/clients")
      // The API lists clients in createdAt order (other consumers rely on
      // that) — sort here so the dropdown reads alphabetically. Locale-aware
      // + case/accent-insensitive.
      .then((r) => {
        if (cancelled) return;
        const sorted = [...(r.clients || [])].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        );
        setClients(sorted);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.role]);

  if (!isAdmin(user?.role)) return null;

  // Staff have no home client (session client is null) — there's no "(home)"
  // anchor option, and until a client is picked the select shows a neutral
  // placeholder instead of silently defaulting to some client.
  const homeId = homeClient?.id || null;
  // On a client-detail page the dropdown must reflect the client in the path,
  // not the dashboard's ?clientId= context.
  const active = detailId || searchParams.get("clientId") || homeId || "";

  function select(id) {
    if (!id) return; // placeholder row — not a client
    // Client-detail page: switch the path segment so the page re-fetches the
    // new client. The active tab is local component state on a route element
    // that stays mounted across the param change, so it's preserved as-is.
    if (detailId) {
      navigate(`/admin/clients/${id}`);
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (homeId && id === homeId) next.delete("clientId");
    else                         next.set("clientId", id);
    // On non-dashboard pages (Agency Home, admin lists, profile…) nothing on
    // screen is client-scoped, so switching would only re-theme — a confusing
    // no-op. Jump to the Overview dashboard so the switch visibly takes
    // effect. Dashboard pages (per the shared registry, via lib/pages.js)
    // just update the param in place.
    if (!isDashboardPath(location.pathname)) {
      const qs = next.toString();
      navigate(qs ? `/overview?${qs}` : "/overview");
      return;
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="px-4 py-3 border-t border-border-light">
      <div
        className="text-[10px] uppercase text-text-muted font-medium mb-1.5"
        style={{ letterSpacing: "0.5px" }}
      >
        Viewing as
      </div>
      <select
        value={active}
        onChange={(e) => select(e.target.value)}
        disabled={loading || clients.length === 0}
        className="w-full h-8 px-2 text-[12px] rounded border border-border-default bg-surface text-text-primary focus:outline-none focus:border-accent"
      >
        {loading && <option>Loading…</option>}
        {!loading && clients.length === 0 && <option>No clients</option>}
        {!loading && clients.length > 0 && !active && (
          <option value="">Select a client…</option>
        )}
        {!loading && clients.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}{homeId && c.id === homeId ? " (home)" : ""}
          </option>
        ))}
      </select>
    </div>
  );
}
